import { Controller } from "@hotwired/stimulus"

// Manages upload button state based on the selected statement format's
// suggested_strategy. Shows which extraction mode will be used, and gates the
// drop area + submit button: the drop area stays disabled until a format is
// chosen, and the submit button until a format is chosen AND a file is added.
export default class extends Controller {
  static targets = ["formatSelect", "submitButton", "submitLabel", "submitIcon", "strategyHint", "thumb", "csvPlaceholder", "dropArea", "drivePickButton", "driveImportButton"]

  static values = {
    strategyPrefix: String,
    strategyLabels: Object,
    buttonFast: String,
    buttonPython: String,
    buttonAi: String,
  }

  static buttonConfig = {
    fast:   { icon: "fas fa-bolt",   color: ["bg-emerald-600", "hover:bg-emerald-700"] },
    python: { icon: "fas fa-table",  color: ["bg-amber-600", "hover:bg-amber-700"] },
    ai:     { icon: "fas fa-upload", color: ["bg-indigo-600", "hover:bg-indigo-700"] },
  }

  connect() {
    this.currentColorClasses = []
    this.fileCount = 0
    this.driveFileCount = 0
    // The client field lives outside this controller's element; watch it directly.
    this.clientField = document.querySelector('input[name="statement[client_id]"]')
    this.boundClientChanged = () => this.refreshAvailability()
    this.clientField?.addEventListener("change", this.boundClientChanged)
    this.updateButton()
    this.refreshAvailability()
  }

  disconnect() {
    this.clientField?.removeEventListener("change", this.boundClientChanged)
  }

  formatChanged() {
    this.updateButton()
    this.refreshAvailability()
  }

  fileCountChanged(event) {
    this.fileCount = event.detail?.count || 0
    this.refreshAvailability()
  }

  driveFilesChanged(event) {
    this.driveFileCount = event.detail?.count || 0
    this.refreshAvailability()
  }

  clientChanged() {
    this.refreshAvailability()
  }

  // Coordinates the two upload paths. Local upload needs a format; Drive import needs a
  // client AND a format. Picking one type locks out the other so the two never mix.
  refreshAvailability() {
    const formatSelected = this.hasFormatSelectTarget && this.formatSelectTarget.value !== ""
    const clientSelected = !!this.clientField?.value
    const localActive = this.fileCount > 0
    const driveActive = this.driveFileCount > 0

    // Local upload — gated by format, locked out while a Drive selection exists.
    this.#toggleDropArea(formatSelected && !driveActive)
    this.#toggleSubmitButton(formatSelected && this.fileCount > 0 && !driveActive)

    // Drive import — gated by client + format, locked out while local files are queued.
    const driveUsable = clientSelected && formatSelected && !localActive
    this.#toggleDrivePick(driveUsable)
    this.#toggleDriveImport(driveUsable && driveActive)
  }

  #toggleDrivePick(enabled) {
    if (!this.hasDrivePickButtonTarget) return

    this.drivePickButtonTarget.disabled = !enabled
    this.drivePickButtonTarget.classList.toggle("opacity-50", !enabled)
    this.drivePickButtonTarget.classList.toggle("pointer-events-none", !enabled)
  }

  #toggleDriveImport(enabled) {
    if (!this.hasDriveImportButtonTarget) return

    this.driveImportButtonTarget.disabled = !enabled
    this.driveImportButtonTarget.classList.toggle("opacity-50", !enabled)
    this.driveImportButtonTarget.classList.toggle("cursor-not-allowed", !enabled)
  }

  #toggleDropArea(enabled) {
    if (!this.hasDropAreaTarget) return

    this.dropAreaTarget.classList.toggle("pointer-events-none", !enabled)
    this.dropAreaTarget.classList.toggle("opacity-50", !enabled)
    this.dropAreaTarget.classList.toggle("cursor-pointer", enabled)
  }

  #toggleSubmitButton(enabled) {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = !enabled
    this.submitButtonTarget.classList.toggle("opacity-50", !enabled)
    this.submitButtonTarget.classList.toggle("cursor-not-allowed", !enabled)
    this.submitButtonTarget.classList.toggle("cursor-pointer", enabled)
  }

  updateButton() {
    if (!this.hasFormatSelectTarget) return

    const select = this.formatSelectTarget
    const selectedOption = select.options[select.selectedIndex]
    const strategy = selectedOption?.dataset?.strategy || "ai"
    const hasColumns = selectedOption?.dataset?.hasColumns === "true"

    // Effective strategy drives the submit button (only fast/python/ai have dedicated button styles).
    // CSV formats are parsed deterministically — never an AI fallback.
    const effective = strategy === "csv"
      ? "csv"
      : (hasColumns && (strategy === "fast" || strategy === "python")) ? strategy : "ai"

    if (this.hasStrategyHintTarget) {
      if (selectedOption?.value) {
        // Show the format's actual suggested_strategy, not the effective fallback.
        const hintLabel = this.strategyLabelsValue[strategy] || this.strategyLabelsValue.ai
        this.strategyHintTarget.textContent = `${this.strategyPrefixValue} ${hintLabel}`
        this.strategyHintTarget.className = effective === "ai"
          ? "text-xs mt-1 text-indigo-600"
          : "text-xs mt-1 text-emerald-600"
      } else if (this.strategyHintTarget.textContent !== "") {
        this.strategyHintTarget.textContent = ""
        this.strategyHintTarget.className = "text-xs mt-1 hidden"
      }
    }

    const thumbUrl = selectedOption?.dataset?.thumbUrl
    const formatType = selectedOption?.dataset?.formatType

    if (this.hasThumbTarget) {
      if (thumbUrl) {
        this.thumbTarget.src = thumbUrl
        this.thumbTarget.alt = selectedOption.text || ""
        this.thumbTarget.classList.remove("hidden")
      } else {
        this.thumbTarget.removeAttribute("src")
        this.thumbTarget.alt = ""
        this.thumbTarget.classList.add("hidden")
      }
    }

    if (this.hasCsvPlaceholderTarget) {
      this.csvPlaceholderTarget.classList.toggle("hidden", !!thumbUrl || formatType !== "csv")
    }
  }
}
